import isEqual from 'lodash/isEqual'

import {
  DeviceAssocationOptionsQuery,
  FloorsQuery,
} from '@/graphql/generated/operations'
import { DeviceType } from '@/graphql/generated/schemas'

import { DeviceEdgeType, Options, TailgateConnsType } from '../types/types'

export const getFloorOptions = (floorsData: FloorsQuery): Options =>
  floorsData?.floors?.edges
    ?.sort((a, b) =>
      a?.node?.facility?.name.localeCompare(b?.node?.facility?.name)
    )
    ?.map((f) => ({
      label: `${f?.node?.name} - (${f?.node?.facility?.name})`,
      value: f?.node?.id,
    }))

export const getDeviceOptions = (devices: DeviceEdgeType): Options =>
  [...devices]
    ?.sort((a, b) => a?.node.name?.localeCompare(b?.node.name || ''))
    ?.map((f) => ({
      label: `${f?.node?.name}`,
      value: f?.node?.id,
      type: f?.node?.type,
    })) || []

export const isPlaced = (device: { x?: number; y?: number }): boolean =>
  device.x != null && device.y != null

export const getDeviceTypeFilter = (type: DeviceType): DeviceType[] => {
  switch (type) {
    case DeviceType.Camera:
      return [DeviceType.Door, DeviceType.Alarm]
    default:
      return [DeviceType.Camera]
  }
}

// Association utils

// Returns the Connection for a given device door id
export const getDeviceConnection = (
  data: DeviceAssocationOptionsQuery,
  deviceId: string
): TailgateConnsType => {
  const tailgateConns: TailgateConnsType[] = data?.device?.tailgateConns?.edges
    ?.map((con) => con.node)
    .reduce((result, current) => result.concat(current), [])

  // Get the associated device for the door
  return tailgateConns?.find((connection) => connection?.door?.id === deviceId)
}

// Returns the Connections for a given device camera id
export const getCameraConnections = (
  data: DeviceAssocationOptionsQuery,
  deviceId: string
): TailgateConnsType[] => {
  const tailgateConns: TailgateConnsType[] =
    data?.device?.tailgateConns?.edges?.map((con) => con.node)

  // Get the camera ids for connected cameras
  const cameraIds = tailgateConns
    ?.map((con) => con.cameras?.edges?.map((e) => e.node.id))
    ?.reduce((result, current) => result.concat(current), [])

  // Return the connection for the selected camera
  return (
    tailgateConns?.filter(() =>
      cameraIds?.some((camId) => camId === deviceId)
    ) || []
  )
}

export const getSelectedCameras = (
  data: DeviceAssocationOptionsQuery,
  deviceId: string
): Options =>
  getDeviceConnection(data, deviceId)?.cameras.edges.map((assicateDevice) => {
    return {
      label: assicateDevice?.node.name,
      value: assicateDevice?.node.id,
      type: assicateDevice?.node.type,
    }
  }) || []

export const getSelectedDevices = (
  data: DeviceAssocationOptionsQuery,
  deviceId: string
): Options =>
  getCameraConnections(data, deviceId)?.map((assicateDevice) => {
    return {
      label: assicateDevice?.door.name,
      value: assicateDevice?.door.id,
      type: assicateDevice?.door.type,
    }
  }) || []

// Returns the filtered device options by the selectedOptions prop
export const filterDeviceOptions = (
  data: DeviceAssocationOptionsQuery,
  selectedOptions: Options
) => {
  const options = getDeviceOptions(data?.devices?.edges)
  if (selectedOptions?.length > 0) {
    const filteredOptions = options.filter((option) =>
      selectedOptions.every((d) => option.value !== d.value)
    )
    return filteredOptions
  } else {
    return options
  }
}

// Returns camera ids of cameras to add and remove. Used in modifyTailgateConnection mutation
export const getCameraIdsForMutation = (
  data: DeviceAssocationOptionsQuery,
  selectedDevices: Options
) => {
  const tailgateConns: TailgateConnsType[] =
    data?.device?.tailgateConns?.edges?.map((con) => con.node)

  // Get the connection ids for connected cameras
  const cameraIds = tailgateConns
    ?.map((con) => con.cameras?.edges?.map((e) => e.node.id))
    ?.reduce((result, current) => result.concat(current), [])

  const cameraIdsToAdd =
    selectedDevices
      ?.filter((d) => cameraIds?.every((id) => d.value !== id))
      .map((opt) => opt.value) || []

  const cameraIdsToRemove =
    cameraIds?.filter((id) => selectedDevices?.every((d) => d.value !== id)) ||
    []

  return { cameraIdsToAdd, cameraIdsToRemove }
}

// Returns a boolean based off if two arrays are equal, ignoring array item order
export const areOptionsEqual = (arr1: Options, arr2: Options) => {
  if (arr1.length !== arr2.length) {
    return false
  }
  const sortedA = arr1.sort((a, b) => a.label.localeCompare(b?.label))
  const sortedB = arr2.sort((a, b) => a.label.localeCompare(b?.label))
  return isEqual(sortedA, sortedB)
}

// Check if devices alreadt have a connection
export const getRemovedDeviceConnections = (
  data: DeviceAssocationOptionsQuery,
  selectedDevices: Options
): TailgateConnsType[] => {
  const tailgateConns: TailgateConnsType[] =
    data?.device?.tailgateConns?.edges?.map((con) => con.node)

  return (
    tailgateConns?.filter((con) =>
      selectedDevices?.every((d) => d.value !== con.door.id)
    ) || []
  )
}

// Returns all device tailgate connections
export const getAllDeviceConnections = (
  data: DeviceAssocationOptionsQuery,
  selectedDevices: Options
) => {
  const allTailgateConns: TailgateConnsType[] = data?.devices?.edges
    ?.map((d) => d.node?.tailgateConns?.edges?.map((e) => e.node))
    ?.reduce((result, current) => result.concat(current), [])

  // All Connections with the selected doors
  return allTailgateConns?.filter((con) =>
    selectedDevices?.some((d) => d.value === con.door.id)
  )
}

// Returns all device tailgate connections
export const isDeviceAlreadyAdded = (
  data: DeviceAssocationOptionsQuery,
  deviceId: string
) => {
  // Get device connections
  const tailgateConns: TailgateConnsType[] =
    data?.device?.tailgateConns?.edges?.map((con) => con.node)
  const deviceIds = tailgateConns?.map((con) => con?.door?.id)
  // Check that device id is not already in the connection device ids
  return deviceIds.includes(deviceId)
}
